import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { fetchJson, assetUrl } from "../lib/api";
import { formatDateFR } from "../lib/format";
import { CARD_SECTION_LABELS, METHOD_LABELS } from "../lib/constants";
import { nationalityWithFlag } from "../lib/countries";
import FighterImage from "../components/FighterImage";
import Countdown from "../components/Countdown";
import ShareButton from "../components/ShareButton";
import { useDocumentMeta } from "../lib/useDocumentMeta";

// Phase 11C — tale-of-the-tape row.
// - If BOTH sides are empty, the whole row is hidden (returns null).
// - If only ONE side is empty, we render "N.C." on that side so the
//   comparison stays legible.
const _isEmpty = (v) => v === null || v === undefined || v === "";
const Line = ({ label, a, b, highlight }) => {
  if (_isEmpty(a) && _isEmpty(b)) return null;
  return (
    <div className="grid grid-cols-11 items-center py-3 border-b border-neutral-900">
      <div
        className={`col-span-4 font-stats text-lg md:text-2xl text-right uppercase tracking-widest ${
          highlight === "a" ? "text-gold" : "text-white"
        } ${_isEmpty(a) ? "text-neutral-600" : ""}`}
      >
        {_isEmpty(a) ? "N.C." : a}
      </div>
      <div className="col-span-3 text-center font-stats text-[10px] md:text-xs text-neutral-600 uppercase tracking-widest">
        {label}
      </div>
      <div
        className={`col-span-4 font-stats text-lg md:text-2xl text-left uppercase tracking-widest ${
          highlight === "b" ? "text-gold" : "text-white"
        } ${_isEmpty(b) ? "text-neutral-600" : ""}`}
      >
        {_isEmpty(b) ? "N.C." : b}
      </div>
    </div>
  );
};

const bigger = (x, y) => {
  if (x == null || y == null) return null;
  if (x > y) return "a";
  if (y > x) return "b";
  return null;
};

export default function FightDetail() {
  const { slug } = useParams();
  const [ft, setFt] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJson(`/fights/${slug}`);
        setFt(data);
      } catch (_) {
        setNotFound(true);
      }
    })();
  }, [slug]);

  const a = ft?.fighter_a || {};
  const b = ft?.fighter_b || {};
  // Broadcast MMA convention — surname in the VS composition. Memoised so
  // string composition + fallback logic doesn't re-run on unrelated renders.
  const { aVs, bVs, vsTitle } = useMemo(() => {
    const aName = (a.last_name || a.first_name || "").toUpperCase();
    const bName = (b.last_name || b.first_name || "").toUpperCase();
    return {
      aVs: aName,
      bVs: bName,
      vsTitle: ft ? `${aName} VS ${bName}` : "",
    };
  }, [ft, a.first_name, a.last_name, b.first_name, b.last_name]);
  useDocumentMeta({
    title: vsTitle,
    description: ft
      ? `${ft.event?.name || ""} · ${(ft.event?.date || "").slice(0, 10)} · ${CARD_SECTION_LABELS[ft.card_section] || ""}`
      : "",
    image: ft?.event?.poster_url ? assetUrl(ft.event.poster_url) : null,
  });

  if (notFound) {
    return (
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 min-h-[60vh]">
        <h1 className="font-display font-black text-5xl md:text-8xl text-white">
          COMBAT INTROUVABLE
        </h1>
        <Link
          to="/events"
          className="inline-flex items-center gap-3 mt-10 px-6 py-3 border border-white text-white font-display uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors duration-200"
        >
          Voir les événements →
        </Link>
      </section>
    );
  }

  if (!ft) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-neutral-600 font-stats text-xs uppercase tracking-widest animate-pulse">
          Chargement…
        </p>
      </div>
    );
  }

  const completed = ft.status === "completed";
  const winnerId = ft.result?.winner_id;
  const highlightWinner = completed
    ? winnerId === a.id
      ? "a"
      : winnerId === b.id
      ? "b"
      : null
    : null;

  return (
    <div data-testid="fight-detail-page">
      {/* HERO VS */}
      <section className="relative border-b border-neutral-900 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-8 pb-16 md:pt-12 md:pb-24">
          <Link
            to={`/events/${ft.event?.slug}`}
            data-testid="fight-back-to-event"
            className="inline-flex items-center gap-2 text-xs font-display uppercase tracking-widest text-neutral-500 hover:text-white transition-colors duration-200"
          >
            <ArrowLeft size={12} /> {ft.event?.name}
          </Link>

          <div className="mt-4 flex flex-wrap items-baseline gap-3">
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
              {CARD_SECTION_LABELS[ft.card_section]}
              {ft.number_rounds ? ` · ${ft.number_rounds} rounds` : ""}
            </p>
            {ft.weight_class?.name && (
              <>
                <span className="w-px h-4 bg-neutral-700 hidden md:inline-block" />
                <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 font-stats">
                  {ft.weight_class?.name}
                </p>
              </>
            )}
            {ft.is_title_fight && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-stats uppercase tracking-[0.3em] text-gold border border-gold/60 px-2 py-0.5"
                data-testid="fight-title-fight-badge"
                title="Combat pour le titre"
              >
                ★ Titre en jeu
              </span>
            )}
            <div className="ml-auto">
              <ShareButton
                title={vsTitle}
                text={`${vsTitle} — ${ft.event?.name || "BUFL"}${ft.event?.date ? ` · ${formatDateFR(ft.event.date)}` : ""}`}
                testId="fight-share"
              />
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-11 items-stretch gap-4">
            {/* A */}
            <Link
              to={`/fighters/${a.slug}`}
              data-testid="fight-a-link"
              className="md:col-span-5 relative border border-neutral-900 bg-neutral-950 aspect-[4/5] md:aspect-[3/4] overflow-hidden group flex flex-col justify-end"
            >
              <FighterImage
                src={assetUrl(a.photo_url)}
                alt={a.first_name}
                className="absolute inset-0"
                imgClassName="object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              {highlightWinner === "a" && (
                <div className="absolute top-3 left-3 px-2 py-0.5 font-stats text-[10px] uppercase tracking-widest border border-gold text-gold bg-black/60">
                  WINNER
                </div>
              )}
              <div className="relative p-4 md:p-8">
                <p className="text-[10px] font-stats uppercase tracking-widest text-neutral-500">
                  {[a.nationality_flag, a.city].filter(Boolean).join(" · ")}
                </p>
                <h2
                  className="mt-2 font-display font-black text-white leading-none break-words max-w-full"
                  style={{ fontSize: "clamp(2rem, 8vw, 6rem)" }}
                >
                  {a.last_name || a.first_name}
                </h2>
                {a.nickname && (
                  <p className="mt-1 text-gold italic text-sm">"{a.nickname}"</p>
                )}
                <p className="mt-3 font-stats text-xs md:text-sm text-neutral-400 uppercase tracking-widest">
                  {a.display_record?.record_str || a.record?.record_str}
                </p>
              </div>
            </Link>

            <div className="md:col-span-1 flex md:flex-col items-center justify-center py-6 md:py-0">
              <div className="hidden md:block w-px h-24 bg-neutral-800" />
              <span className="font-display font-black text-6xl md:text-8xl text-gold px-4">
                VS
              </span>
              <div className="hidden md:block w-px h-24 bg-neutral-800" />
            </div>

            <Link
              to={`/fighters/${b.slug}`}
              data-testid="fight-b-link"
              className="md:col-span-5 relative border border-neutral-900 bg-neutral-950 aspect-[4/5] md:aspect-[3/4] overflow-hidden group flex flex-col justify-end"
            >
              <FighterImage
                src={assetUrl(b.photo_url)}
                alt={b.first_name}
                className="absolute inset-0"
                imgClassName="object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              {highlightWinner === "b" && (
                <div className="absolute top-3 right-3 px-2 py-0.5 font-stats text-[10px] uppercase tracking-widest border border-gold text-gold bg-black/60">
                  WINNER
                </div>
              )}
              <div className="relative p-4 md:p-8 md:text-right">
                <p className="text-[10px] font-stats uppercase tracking-widest text-neutral-500">
                  {[b.nationality_flag, b.city].filter(Boolean).join(" · ")}
                </p>
                <h2
                  className="mt-2 font-display font-black text-white leading-none break-words max-w-full"
                  style={{ fontSize: "clamp(2rem, 8vw, 6rem)" }}
                >
                  {b.last_name || b.first_name}
                </h2>
                {b.nickname && (
                  <p className="mt-1 text-gold italic text-sm">"{b.nickname}"</p>
                )}
                <p className="mt-3 font-stats text-xs md:text-sm text-neutral-400 uppercase tracking-widest">
                  {b.display_record?.record_str || b.record?.record_str}
                </p>
              </div>
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-neutral-900 pt-6">
            <p className="font-stats text-xs md:text-sm text-neutral-400 uppercase tracking-widest">
              {formatDateFR(ft.event?.date, { withGmtTime: true })} · {ft.event?.venue} · {ft.event?.city}
            </p>
            {!completed && (
              <Countdown target={ft.event?.date} testId="fight-countdown" />
            )}
          </div>
        </div>
      </section>

      {/* Result box */}
      {completed && ft.result && (
        <section className="border-b border-neutral-900 bg-neutral-950/60">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-14">
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
              Résultat officiel
            </p>
            <div className="mt-4 flex flex-wrap items-baseline gap-x-8 gap-y-2">
              <p className="font-display font-black text-white text-4xl md:text-7xl">
                {highlightWinner === "a" ? a.first_name : highlightWinner === "b" ? b.first_name : "—"}
              </p>
              <span className="font-display font-bold text-gold text-2xl md:text-4xl uppercase">
                — VICTOIRE —
              </span>
              <span className="font-stats text-white text-xl md:text-3xl uppercase tracking-widest">
                {METHOD_LABELS[ft.result.method] || ft.result.method}
              </span>
            </div>
            <p className="mt-2 font-stats text-sm md:text-base text-neutral-400 uppercase tracking-widest">
              Round {ft.result.round} · {ft.result.time}
            </p>
          </div>
        </section>
      )}

      {/* Narrative (Phase 7) — approved recap after a completed fight, or approved preview before */}
      {completed && ft.recap_text && (
        <section className="border-b border-neutral-900" data-testid="fight-recap-section">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16">
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
              Récap officiel
            </p>
            <h2 className="mt-4 font-display font-black text-2xl md:text-4xl text-white leading-none">
              LE COMBAT
            </h2>
            <p className="mt-6 text-neutral-300 text-base md:text-lg leading-relaxed whitespace-pre-wrap max-w-3xl" data-testid="fight-recap-text">
              {ft.recap_text}
            </p>
          </div>
        </section>
      )}
      {!completed && ft.preview_text && (
        <section className="border-b border-neutral-900" data-testid="fight-preview-section">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16">
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
              L'enjeu
            </p>
            <h2 className="mt-4 font-display font-black text-2xl md:text-4xl text-white leading-none">
              APERÇU DU COMBAT
            </h2>
            <p className="mt-6 text-neutral-300 text-base md:text-lg leading-relaxed whitespace-pre-wrap max-w-3xl" data-testid="fight-preview-text">
              {ft.preview_text}
            </p>
          </div>
        </section>
      )}

      {/* Tale of the tape */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-20">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
          Comparatif technique
        </p>
        <h2 className="mt-4 font-display font-black text-3xl md:text-5xl text-white leading-none">
          TALE OF THE TAPE
        </h2>

        <div className="mt-8 border-t border-neutral-900">
          <Line
            label="Record"
            a={a.display_record?.record_str || a.record?.record_str}
            b={b.display_record?.record_str || b.record?.record_str}
          />
          <Line
            label="Âge"
            a={a.age ? `${a.age}` : null}
            b={b.age ? `${b.age}` : null}
            highlight={bigger(a.age, b.age)}
          />
          <Line
            label="Taille"
            a={a.height_cm ? `${a.height_cm} CM` : null}
            b={b.height_cm ? `${b.height_cm} CM` : null}
            highlight={bigger(a.height_cm, b.height_cm)}
          />
          <Line
            label="Allonge"
            a={a.reach_cm ? `${a.reach_cm} CM` : null}
            b={b.reach_cm ? `${b.reach_cm} CM` : null}
            highlight={bigger(a.reach_cm, b.reach_cm)}
          />
          <Line
            label="Poids"
            a={a.current_weight_kg ? `${a.current_weight_kg} KG` : null}
            b={b.current_weight_kg ? `${b.current_weight_kg} KG` : null}
          />
          <Line label="Stance" a={a.stance} b={b.stance} />
          <Line
            label="Pays"
            a={nationalityWithFlag(a.nationality, a.nationality_flag)}
            b={nationalityWithFlag(b.nationality, b.nationality_flag)}
          />
          <Line label="Ville" a={a.city} b={b.city} />
          <Line label="Académie" a={a.academy?.name} b={b.academy?.name} />
        </div>

        <div className="mt-10">
          <Link
            to={`/events/${ft.event?.slug}`}
            data-testid="fight-event-cta"
            className="inline-flex items-center gap-3 px-6 py-3 border border-white text-white font-display uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors duration-200"
          >
            Voir l'événement →
          </Link>
        </div>
      </section>
    </div>
  );
}
